import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  MessageSquare, Users, Ban, Plus, Trash2, 
  MessageCircle, RefreshCw, Volume2, VolumeX, Download
} from "lucide-react";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const ChatForumModeration = ({ token }) => {
  const [chatRooms, setChatRooms] = useState([]);
  const [mutedUsers, setMutedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRoomName, setNewRoomName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showClearChat, setShowClearChat] = useState(false);
  const [purgeBackups, setPurgeBackups] = useState(false);
  const [clearingChat, setClearingChat] = useState(false);
  const [exportingChat, setExportingChat] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [roomsRes, mutedRes] = await Promise.all([
        axios.get(`${API}/chat/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/muted-users`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setChatRooms(roomsRes.data);
      setMutedUsers(mutedRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!newRoomName.trim()) return;

    try {
      setCreating(true);
      await axios.post(
        `${API}/admin/chat/rooms`,
        { name: newRoomName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewRoomName("");
      setMessage("✅ Chat room created successfully!");
      fetchData();
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage(`❌ ${err.response?.data?.detail || "Failed to create room"}`);
      setTimeout(() => setMessage(""), 3000);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!confirm("Are you sure you want to delete this chat room? All messages will be lost.")) return;

    try {
      await axios.delete(`${API}/admin/chat/rooms/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage("✅ Chat room deleted");
      fetchData();
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage(`❌ ${err.response?.data?.detail || "Failed to delete room"}`);
      setTimeout(() => setMessage(""), 3000);
    }
  };

  const handleExportChat = async () => {
    setExportingChat(true);
    try {
      const res = await axios.get(`${API}/admin/chat/export`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob"
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `georgedives-chat-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage("❌ Failed to export chat history");
      setTimeout(() => setMessage(""), 4000);
    } finally {
      setExportingChat(false);
    }
  };

  const handleClearChat = async () => {
    setClearingChat(true);
    try {
      const res = await axios.post(`${API}/admin/chat/clear`, { purge_backups: purgeBackups }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(`✅ ${res.data.message}${purgeBackups ? " — scrubbing backups in the background" : ""}`);
      setShowClearChat(false);
      setTimeout(() => setMessage(""), 6000);
    } catch (err) {
      setMessage(`❌ ${err.response?.data?.detail || "Failed to clear chat"}`);
      setTimeout(() => setMessage(""), 4000);
    } finally {
      setClearingChat(false);
    }
  };

  const handleUnmuteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}/mute`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage("✅ User unmuted");
      fetchData();
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage(`❌ ${err.response?.data?.detail || "Failed to unmute user"}`);
      setTimeout(() => setMessage(""), 3000);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-500 mx-auto" />
        <p className="text-gray-500 mt-2">Loading moderation data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {message && (
        <div className={`p-3 rounded-lg ${
          message.includes('✅') 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message}
        </div>
      )}

      {/* Chat Rooms Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-cyan-600" />
            Chat Rooms
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Create New Room */}
          <form onSubmit={handleCreateRoom} className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-6">
            <input
              type="text"
              value={newRoomName}
              onChange={(e) => setNewRoomName(e.target.value)}
              placeholder="New room name..."
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
            />
            <Button type="submit" disabled={creating} className="bg-cyan-500 hover:bg-cyan-600 shrink-0">
              <Plus className="w-4 h-4 mr-2" />
              {creating ? "Creating..." : "Add Room"}
            </Button>
          </form>

          {/* Rooms List */}
          <div className="space-y-3">
            {chatRooms.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No chat rooms yet</p>
            ) : (
              chatRooms.map(room => (
                <div
                  key={room.id}
                  className={`p-4 border rounded-lg flex items-center justify-between ${
                    room.is_global ? "bg-cyan-50 border-cyan-200" : "bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className={`w-5 h-5 ${room.is_global ? "text-cyan-600" : "text-gray-500"}`} />
                    <div>
                      <p className="font-medium">{room.name}</p>
                      <p className="text-sm text-gray-500">
                        {room.is_global ? "Global Room (Default)" : "Custom Room"}
                      </p>
                    </div>
                  </div>
                  {room.is_global ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setShowClearChat(true)}
                      data-testid="clear-chat-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Clear history
                    </Button>
                  ) : (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteRoom(room.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
          {showClearChat && (
            <div className="mt-4 p-4 rounded-lg border-2 border-red-200 bg-red-50 space-y-3" data-testid="clear-chat-panel">
              <p className="font-semibold text-red-800">Clear all chat history?</p>
              <p className="text-sm text-red-700">This permanently deletes every message in every chat room for all members. Everyone's chat will empty instantly.</p>
              <Button variant="outline" size="sm" className="bg-white" disabled={exportingChat} onClick={handleExportChat} data-testid="export-chat-btn">
                <Download className="w-4 h-4 mr-1" /> {exportingChat ? "Preparing..." : "Download chat history first (JSON)"}
              </Button>
              <label className="flex items-start gap-2 text-sm text-red-800">
                <input type="checkbox" className="mt-0.5" checked={purgeBackups} onChange={(e) => setPurgeBackups(e.target.checked)} data-testid="clear-chat-purge-backups" />
                <span>Also scrub chat messages from all existing cloud database backups (runs in the background, may take a few minutes)</span>
              </label>
              <div className="flex gap-2">
                <Button variant="destructive" size="sm" disabled={clearingChat} onClick={handleClearChat} data-testid="clear-chat-confirm">
                  {clearingChat ? "Clearing..." : "Yes, delete everything"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowClearChat(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Muted Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <VolumeX className="w-5 h-5 text-red-500" />
            Muted Users
            {mutedUsers.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-sm rounded-full">
                {mutedUsers.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Muted users cannot post in the forum or chat. You can mute users from the Users Management tab.
          </p>
          
          {mutedUsers.length === 0 ? (
            <div className="text-center py-8">
              <Volume2 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No muted users</p>
            </div>
          ) : (
            <div className="space-y-3">
              {mutedUsers.map(muted => (
                <div
                  key={muted.user_id}
                  className="p-4 border rounded-lg flex items-center justify-between bg-red-50 border-red-100"
                >
                  <div>
                    <p className="font-medium">{muted.user_name || muted.user_id}</p>
                    <p className="text-sm text-gray-500">{muted.user_email}</p>
                    {muted.reason && (
                      <p className="text-sm text-red-600 mt-1">Reason: {muted.reason}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Muted on {new Date(muted.muted_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUnmuteUser(muted.user_id)}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    <Volume2 className="w-4 h-4 mr-1" />
                    Unmute
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Forum Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Forum threads and replies can be managed directly from the Forum page. 
            As an admin, you can:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
            <li>Pin important threads to keep them at the top</li>
            <li>Lock threads to prevent new replies</li>
            <li>Delete threads or individual replies</li>
            <li>Delete chat messages from any room</li>
          </ul>
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={() => window.location.href = "/community/forum"}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Go to Forum
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatForumModeration;
